import { css, StyleSheet } from 'aphrodite';
import { FC, useState } from 'react';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
	DropdownSeparator,
} from '../../../../web/components-v2/Dropdown';
import { CheckmarkIcon } from '../../../../web/components/svgs/icons/CheckmarkIcon';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { bs } from '../../../../web/styles/styles';
import { Row } from '../../../components/Layout';
import { InfoPopover } from '../../../components/InfoPopover/InfoPopover';
import { styleSheet } from '../../../components/blogs/styles';
import { getAllPresets, StylePreset } from './presets';
import { PresetPreviewModal } from './PresetPreviewModal';

interface PresetSelectorProps {
	currentPresetId: string;
	onPresetSelect: (preset: StylePreset) => void;
	disabled?: boolean;
	hasUnsavedChanges?: boolean;
}

function getPresetQuadrants(preset: StylePreset): [string, string, string, string] {
	const fallback = preset.colors.header?.textColor || THEME.colors.border.light;
	return [
		preset.colors.header?.backgroundColor || fallback,
		preset.colors.header?.hoverColor || fallback,
		preset.colors.footer?.backgroundColor || fallback,
		preset.colors.footer?.linkColor || fallback,
	];
}

function getFontCaption(preset: StylePreset): string | null {
	const header = preset.fonts.header?.fontName;
	const footer = preset.fonts.footer?.fontName;
	if (!header && !footer) {
		return null;
	}
	if (header && footer && header !== footer) {
		return `${header} + ${footer}`;
	}
	return header ?? footer ?? null;
}

const PresetPaletteTile: FC<{ preset: StylePreset | null }> = ({ preset }) => {
	if (!preset) {
		return (
			<div className={css(s.tile)} data-testid='preset-tile-custom'>
				{[0, 1, 2, 3].map(i => (
					<div key={i} className={css(s.quadrant)} style={{ backgroundColor: THEME.colors.border.light }} />
				))}
			</div>
		);
	}

	const [topLeft, topRight, bottomLeft, bottomRight] = getPresetQuadrants(preset);
	return (
		<div className={css(s.tile)} data-testid={`preset-tile-${preset.id}`}>
			<div className={css(s.quadrant)} style={{ backgroundColor: topLeft }} />
			<div className={css(s.quadrant)} style={{ backgroundColor: topRight }} />
			<div className={css(s.quadrant)} style={{ backgroundColor: bottomLeft }} />
			<div className={css(s.quadrant)} style={{ backgroundColor: bottomRight }} />
		</div>
	);
};

export const PresetSelector: FC<PresetSelectorProps> = ({
	currentPresetId,
	onPresetSelect,
	disabled,
	hasUnsavedChanges,
}) => {
	const [previewPreset, setPreviewPreset] = useState<StylePreset | null>(null);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const allPresets = getAllPresets();
	const currentPreset = allPresets.find(p => p.id === currentPresetId);

	const handlePresetClick = (preset: StylePreset) => {
		if (preset.id === 'custom') {
			return;
		}
		setPreviewPreset(preset);
		setDropdownOpen(false);
	};

	const handleApplyPreset = (_: never, canceled: boolean) => {
		if (!canceled && previewPreset) {
			onPresetSelect(previewPreset);
		}
		setPreviewPreset(null);
	};

	return (
		<>
			<Row className={css(bs.itemsStart, bs.gap4, bs.mb6, bs.p4, bs.bgGray50, bs.rounded)}>
				<div className={css(bs.flex, bs.flexCol, bs.flex1)}>
					<Row className={css(bs.itemsCenter, bs.gap2, bs.mb2)}>
						<label className={css(styleSheet.label, bs.mb0)}>Style Preset</label>
						<InfoPopover message='Apply a pre-built style preset to quickly customize your blog appearance. You can further customize any preset after applying it.' />
					</Row>
					<p className={css(bs.textSm, bs.textGray600)}>
						Choose from professional templates or create your own custom style
					</p>
				</div>

				<div className={css(bs.flex, bs.flexCol, bs.pt1)}>
					<Dropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
						<DropdownSelectTrigger disabled={disabled}>
							<span className={css(bs.truncateText)}>
								{currentPreset?.name || 'Select a preset'}
								{currentPresetId === 'custom' && hasUnsavedChanges && ' *'}
							</span>
						</DropdownSelectTrigger>

						<DropdownContent align='start' styles={[s.dropdownContent]}>
							<div className={css(s.sectionLabel)}>Pre-built Presets</div>
							{allPresets
								.filter(p => p.id !== 'custom')
								.map(preset => {
									const isSelected = currentPresetId === preset.id;
									const fontCaption = getFontCaption(preset);
									return (
										<DropdownItem
											key={preset.id}
											onClick={() => handlePresetClick(preset)}
											styles={[s.presetCard, isSelected && s.presetCardSelected]}
										>
											<PresetPaletteTile preset={preset} />
											<div className={css(s.textColumn)}>
												<span className={css(s.presetName)}>{preset.name}</span>
												<span className={css(s.presetDescription)}>{preset.description}</span>
												{fontCaption && <span className={css(s.fontCaption)}>{fontCaption}</span>}
											</div>
											{isSelected && (
												<CheckmarkIcon className={css(s.checkIcon)} fillColor={THEME.colors.primary.main} />
											)}
										</DropdownItem>
									);
								})}

							{currentPresetId === 'custom' && (
								<>
									<DropdownSeparator />
									<div className={css(s.sectionLabel)}>Your Style</div>
									<DropdownItem styles={[s.presetCard, s.presetCardSelected]}>
										<PresetPaletteTile preset={null} />
										<div className={css(s.textColumn)}>
											<span className={css(s.presetName)}>Custom</span>
											<span className={css(s.presetDescription)}>Your custom settings</span>
										</div>
										<CheckmarkIcon className={css(s.checkIcon)} fillColor={THEME.colors.primary.main} />
									</DropdownItem>
								</>
							)}
						</DropdownContent>
					</Dropdown>

					{currentPresetId === 'custom' && hasUnsavedChanges && (
						<p className={css(bs.textXs, bs.textGray600, bs.mt1)}>* Unsaved changes</p>
					)}
				</div>
			</Row>

			{previewPreset && (
				<PresetPreviewModal
					preset={previewPreset}
					modalProps={{
						isOpen: !!previewPreset,
						onRequestClose: handleApplyPreset,
					}}
					hasUnsavedChanges={hasUnsavedChanges}
				/>
			)}
		</>
	);
};

const s = StyleSheet.create({
	dropdownContent: {
		width: 320,
		minWidth: 320,
	},
	sectionLabel: {
		padding: `${THEME.spacing.sm}px ${THEME.spacing.sm}px ${THEME.spacing.xs}px`,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: 0.5,
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	presetCard: {
		alignItems: 'flex-start',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.sm,
		borderRadius: THEME.common.borderRadius,
		borderLeft: '3px solid transparent',
	},
	presetCardSelected: {
		backgroundColor: THEME.colors.primary.light,
		borderLeftColor: THEME.colors.primary.main,
	},
	tile: {
		boxSizing: 'border-box',
		width: 40,
		height: 40,
		flexShrink: 0,
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gridTemplateRows: '1fr 1fr',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.light}`,
		overflow: 'hidden',
	},
	quadrant: {
		width: '100%',
		height: '100%',
	},
	textColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		flex: 1,
		minWidth: 0,
	},
	presetName: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	presetDescription: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		lineHeight: 1.35,
		whiteSpace: 'normal',
	},
	fontCaption: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	checkIcon: {
		flexShrink: 0,
		width: 14,
		height: 14,
		marginTop: 2,
	},
});
